import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Sparkles, Plus, Edit2, Trash2, CheckCircle2, X } from 'lucide-react';
import { ServiceCategory } from '@ftt/shared';

export const AdminCmsServicesPage: React.FC = () => {
  const [editingService, setEditingService] = useState<any>(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [category, setCategory] = useState<ServiceCategory>('EDITORIAL');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [timeline, setTimeline] = useState('');
  const [isActive, setIsActive] = useState(true);

  const queryClient = useQueryClient();

  const { data: servicesRes, isLoading } = useQuery({
    queryKey: ['admin-services'],
    queryFn: () => api.get('/public/services').then((res) => res.data.data),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title,
        slug: slug || title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        category,
        summary,
        description,
        timeline,
        isActive,
        includedItems: ['Standard publishing deliverables', 'Direct editorial communication'],
        processSteps: [{ title: 'Initial Review', description: 'Reviewing scope and timeline' }],
        deliverables: ['Standard deliverables package'],
      };

      if (editingService?.id) {
        return api.put(`/admin/services/${editingService.id}`, payload);
      } else {
        return api.post('/admin/services', payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-services'] });
      queryClient.invalidateQueries({ queryKey: ['public-services'] });
      setEditingService(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/admin/services/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-services'] });
      queryClient.invalidateQueries({ queryKey: ['public-services'] });
    },
  });

  const services = servicesRes || [];

  const openEditor = (srv?: any) => {
    if (srv) {
      setEditingService(srv);
      setTitle(srv.title);
      setSlug(srv.slug);
      setCategory(srv.category);
      setSummary(srv.summary);
      setDescription(srv.description);
      setTimeline(srv.timeline);
      setIsActive(srv.isActive);
    } else {
      setEditingService({ id: '' });
      setTitle('');
      setSlug('');
      setCategory('EDITORIAL');
      setSummary('');
      setDescription('');
      setTimeline('2 to 4 weeks');
      setIsActive(true);
    }
  };

  return (
    <div className="space-y-8">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white">
            Publishing Services CMS
          </h1>
          <p className="text-xs sm:text-sm text-slate-400">
            Create and edit publishing service categories, deliverables, and author descriptions.
          </p>
        </div>

        <button
          onClick={() => openEditor()}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs shadow-sm transition-colors self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Add New Service</span>
        </button>
      </div>

      {/* Services Grid */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-400">Loading services...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((srv: any) => (
            <div
              key={srv.id}
              className="p-6 rounded-2xl bg-slate-950 border border-slate-800 space-y-4 flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono font-bold uppercase px-2.5 py-1 rounded bg-slate-900 text-brand-400 border border-slate-800">
                    {srv.category}
                  </span>
                  <span className={`text-[10px] font-mono font-bold uppercase ${srv.isActive ? 'text-emerald-400' : 'text-slate-500'}`}>
                    {srv.isActive ? '● Active' : '○ Disabled'}
                  </span>
                </div>

                <h3 className="font-serif text-xl font-bold text-white">{srv.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed line-clamp-3">{srv.summary}</p>
                <div className="text-[11px] font-mono text-slate-500">Timeline: {srv.timeline}</div>
              </div>

              <div className="pt-4 border-t border-slate-800 flex items-center justify-between text-xs">
                <button
                  onClick={() => openEditor(srv)}
                  className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-white font-bold inline-flex items-center gap-1"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => deleteMutation.mutate(srv.id)}
                  className="px-3 py-1.5 rounded bg-rose-950/40 text-rose-300 hover:bg-rose-900/60 font-bold inline-flex items-center gap-1 border border-rose-800/40"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {editingService && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-slate-950 rounded-2xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-800 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="font-serif text-xl font-bold text-white">
                {editingService.id ? 'Edit Publishing Service' : 'Add New Service'}
              </h3>
              <button onClick={() => setEditingService(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">Service Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                  placeholder="e.g. Master Typesetting & Layout"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-400 uppercase font-mono mb-1">Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as any)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                  >
                    <option value="EDITORIAL">EDITORIAL</option>
                    <option value="PUBLISHING">PUBLISHING</option>
                    <option value="DESIGN">DESIGN</option>
                    <option value="PRODUCTION">PRODUCTION</option>
                    <option value="MARKETING">MARKETING</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-400 uppercase font-mono mb-1">Timeline</label>
                  <input
                    type="text"
                    value={timeline}
                    onChange={(e) => setTimeline(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                    placeholder="e.g. 3 to 4 weeks"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">Short Summary</label>
                <textarea
                  rows={2}
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                  placeholder="Short description displayed in catalog grid..."
                />
              </div>

              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">Full Scope &amp; Details</label>
                <textarea
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                  placeholder="Detailed description of process and expectations..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <button
                onClick={() => setEditingService(null)}
                className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-900"
              >
                Cancel
              </button>
              <button
                onClick={() => saveMutation.mutate()}
                disabled={!title || saveMutation.isPending}
                className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs"
              >
                Save Service
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
